import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'

/**
 * StatusContext - Global status message management
 * 
 * Provides a centralized way to show status messages throughout the app,
 * replacing toast notifications with a consistent status bar experience.
 */

const StatusContext = createContext()

export const useStatus = () => {
  const context = useContext(StatusContext)
  if (!context) {
    throw new Error('useStatus must be used within a StatusProvider')
  }
  return context
}

export const StatusProvider = ({ children }) => {
  const [currentStatus, setCurrentStatus] = useState({
    message: '',
    type: 'info', // info, success, warning, error, loading
    isVisible: false,
    id: null
  })
  
  const timeoutRef = useRef(null)
  const idCounterRef = useRef(0)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Clear any existing timeout
  const clearStatusTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Show a status message
  const showStatus = useCallback((message, type = 'info', options = {}) => {
    const {
      duration = null, // null means no auto-dismiss
      id = null,
      replace = true // whether to replace current message or queue
    } = options

    // Generate unique ID if not provided
    const statusId = id || `status-${++idCounterRef.current}`

    // Clear existing timeout if replacing
    if (replace) {
      clearStatusTimeout()
    }

    // Set the new status
    setCurrentStatus({
      message,
      type,
      isVisible: true,
      id: statusId
    })

    // Auto-dismiss after duration if specified
    if (duration && duration > 0) {
      timeoutRef.current = setTimeout(() => {
        hideStatus(statusId)
      }, duration)
    }

    return statusId
  }, [clearStatusTimeout])

  // Hide status message
  const hideStatus = useCallback((id = null) => {
    clearStatusTimeout()
    setCurrentStatus(prev => {
      // If ID is provided, only hide if it matches current status
      if (id && prev.id !== id) {
        return prev
      }
      
      return {
        ...prev,
        isVisible: false
      }
    })
  }, [clearStatusTimeout])

  // Convenience methods for different status types
  const showSuccess = useCallback((message, options = {}) => {
    return showStatus(message, 'success', { duration: 3000, ...options })
  }, [showStatus])

  const showError = useCallback((message, options = {}) => {
    return showStatus(message, 'error', { duration: 5000, ...options })
  }, [showStatus])

  const showWarning = useCallback((message, options = {}) => {
    return showStatus(message, 'warning', { duration: 4000, ...options })
  }, [showStatus])

  const showInfo = useCallback((message, options = {}) => {
    return showStatus(message, 'info', { duration: 3000, ...options })
  }, [showStatus])

  const showLoading = useCallback((message, options = {}) => {
    return showStatus(message, 'loading', { duration: null, ...options })
  }, [showStatus])

  // Update existing status message (useful for loading states)
  const updateStatus = useCallback((message, type = null) => {
    setCurrentStatus(prev => ({
      ...prev,
      message,
      type: type || prev.type
    }))
  }, [])

  // Dismiss with a specific ID (for managing multiple status messages)
  const dismissStatus = useCallback((id) => {
    hideStatus(id)
  }, [hideStatus])

  const value = {
    // Current status state
    currentStatus,
    
    // Core methods
    showStatus,
    hideStatus,
    updateStatus,
    dismissStatus,
    
    // Convenience methods
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    
    // Utility
    clearStatusTimeout
  }

  return (
    <StatusContext.Provider value={value}>
      {children}
    </StatusContext.Provider>
  )
} 